const allowedTags = [
  'A', 'ARTICLE', 'BLOCKQUOTE', 'BR', 'CODE', 'DD', 'DIV', 'DL', 'DT', 'EM',
  'H1', 'H2', 'H3', 'H4', 'HR', 'IMG', 'LI', 'OL', 'P', 'PRE', 'SMALL', 'SPAN',
  'STRONG', 'TABLE', 'TBODY', 'TD', 'TH', 'THEAD', 'TR', 'UL'
]
const removedTags = ['BUTTON', 'EMBED', 'FORM', 'IFRAME', 'INPUT', 'OBJECT', 'SCRIPT', 'STYLE']

// HmRag names an image by reference, never by url: `product:<slug>` or `doc:<id>:<n>`. A src that
// is not one of those is dropped along with its element rather than merely blanked, so nothing
// downstream can mistake a stripped tag for an image that failed to load.
const imageReference = /^(?:product:[A-Za-z0-9_-]+|doc:[A-Za-z0-9_-]+:[0-9]+)$/

function isEvidenceAnchor(element) {
  return element.tagName === 'A' && (` ${element.getAttribute('class') || ''} `).indexOf(' evi ') !== -1
}

/**
 * @param html            untrusted markup from HmRag
 * @param resolveImage    optional `reference => url`. Without it every image is dropped, so a
 *                        caller that has nowhere to serve images from cannot accidentally emit
 *                        one. The wiki resolves against its slug; chat will resolve against its
 *                        query id, which is why the url is the caller's to build and not ours.
 */
export function sanitizeHmRagHtml(html, resolveImage) {
  if (!html) return ''
  const container = document.createElement('div')
  container.innerHTML = html

  Array.prototype.slice.call(container.querySelectorAll('*')).forEach(element => {
    if (removedTags.indexOf(element.tagName) !== -1) {
      element.parentNode.removeChild(element)
      return
    }
    if (allowedTags.indexOf(element.tagName) === -1) {
      while (element.firstChild) element.parentNode.insertBefore(element.firstChild, element)
      element.parentNode.removeChild(element)
      return
    }

    // Read the reference before the attribute sweep blanks it. The resolver is never handed
    // anything that failed the pattern, so it can build a url without re-validating.
    let imageUrl = null
    if (element.tagName === 'IMG' && resolveImage) {
      const reference = element.getAttribute('src') || ''
      if (imageReference.test(reference)) imageUrl = resolveImage(reference)
    }

    const evidenceAnchor = isEvidenceAnchor(element)
    Array.prototype.slice.call(element.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase()
      const keepExternalLink = element.tagName === 'A' && ['href', 'title'].indexOf(name) !== -1
      const keepEvidence = evidenceAnchor && [
        'class', 'href', 'data-claim-id', 'data-citation-n'
      ].indexOf(name) !== -1
      const keepImageAlt = element.tagName === 'IMG' && name === 'alt'
      if (!keepExternalLink && !keepEvidence && !keepImageAlt) element.removeAttribute(attribute.name)
    })

    // The url is parked in a data attribute rather than src. An <img src> is fetched by the
    // browser without the bearer token axios attaches, so the API would answer 401 and the reader
    // would see a broken-image icon. The component fetches it as a blob and swaps in an object url.
    if (element.tagName === 'IMG') {
      if (imageUrl) element.setAttribute('data-hmrag-src', imageUrl)
      else element.parentNode.removeChild(element)
      return
    }

    if (element.tagName === 'A') {
      if (evidenceAnchor) {
        element.setAttribute('class', 'evi')
        element.setAttribute('href', '#')
      } else {
        const href = element.getAttribute('href') || ''
        if (!/^https:\/\//i.test(href)) element.removeAttribute('href')
        else {
          element.setAttribute('target', '_blank')
          element.setAttribute('rel', 'noopener noreferrer')
        }
      }
    }
  })

  return container.innerHTML
}

export function renderChatAnswer(answerHtml, citations, markerClaims, resolveImage) {
  const container = document.createElement('div')
  container.innerHTML = sanitizeHmRagHtml(answerHtml, resolveImage)
  const citedNumbers = new Set((citations || []).map(citation => String(citation.number)))
  const claims = markerClaims || []
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false)
  const textNodes = []
  let node
  while ((node = walker.nextNode())) textNodes.push(node)

  let markerIndex = 0
  textNodes.forEach(textNode => {
    const parentTag = textNode.parentNode && textNode.parentNode.tagName
    if (['A', 'CODE', 'PRE'].indexOf(parentTag) !== -1) return
    const text = textNode.nodeValue || ''
    const pattern = /\[(\d+)\]/g
    let match
    let offset = 0
    let changed = false
    const fragment = document.createDocumentFragment()

    while ((match = pattern.exec(text))) {
      const claimIndex = claims[markerIndex++]
      if (!citedNumbers.has(match[1])) continue
      changed = true
      fragment.appendChild(document.createTextNode(text.slice(offset, match.index)))
      const anchor = document.createElement('a')
      anchor.className = 'evi'
      anchor.href = '#'
      anchor.setAttribute('data-citation-n', match[1])
      if (claimIndex !== null && claimIndex !== undefined) {
        anchor.setAttribute('data-claim-index', String(claimIndex))
      }
      anchor.textContent = match[0]
      fragment.appendChild(anchor)
      offset = match.index + match[0].length
    }

    if (changed) {
      fragment.appendChild(document.createTextNode(text.slice(offset)))
      textNode.parentNode.replaceChild(fragment, textNode)
    }
  })

  return container.innerHTML
}

// The product wiki pages HmRag says this answer is about, turned into links into THIS app's own
// wiki page. Deliberately NOT HmRag's /library/<slug>: that path sits behind HmRag's site-wide
// login gate, and a customer has no account there. HmRag sends only the page address and the
// display name for exactly this reason -- each front end builds its own link.
// A pure function rather than component logic: importing a .vue into this test suite fails with
// "Cyclic dependency", so anything that needs a test has to live here.
export function productWikiLinks(productWiki) {
  return (productWiki || [])
    .filter(entry => entry && entry.slug)
    .map(entry => ({
      slug: entry.slug,
      name: entry.name || entry.slug,
      href: `/item-search/wiki?slug=${encodeURIComponent(entry.slug)}`
    }))
}

// One grouping of the citations, shared by the on-screen Sources line and the clipboard, so the
// two always list the same documents in the same order.
export function groupCitations(citations) {
  const order = []
  const byDocument = {}
  const list = citations || []
  list.forEach(citation => {
    const key = citation.source_url || citation.label
    if (!byDocument[key]) {
      byDocument[key] = {
        numbers: [],
        label: citation.label,
        url: citation.source_url,
        docType: citation.doc_type
      }
      order.push(key)
    }
    byDocument[key].numbers.push(citation.number)
  })
  return order.map(key => byDocument[key])
}
